import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageHeader from '../../components/misc/PageHeader';
import BookYearSelector, { ReportRange } from '../../components/financial/BookYearSelector';
import FinancialBalancePage from './FinancialBalancePage';
import FinancialProfitLossPage from './FinancialProfitLossPage';

export default function ReportsPage(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();

  const getInitialTab = (): 'profitLoss' | 'balance' => {
    if (location.state?.activeTab) {
      return location.state.activeTab;
    }
    if (location.pathname.includes('/financial/balance')) {
      return 'balance';
    }
    return 'profitLoss';
  };

  const [activeTab, setActiveTab] = React.useState<'profitLoss' | 'balance'>(getInitialTab());
  const [range, setRange] = React.useState<ReportRange | null>(null);

  React.useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  return (
    <>
      <div>
        <PageHeader
          title={t('page.financial.reports.title')}
          subtitle={t('page.financial.reports.subtitle')}
        />

        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          <BookYearSelector value={range} onChange={setRange} />
        </div>

        {/* Tabs */}
        <div className="mb-6 flex gap-4 border-b border-primary-300">
          {(['profitLoss', 'balance'] as const).map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setActiveTab(tabKey)}
              className={`relative px-4 pb-1 pt-2 font-medium transition-all duration-150 ${
                activeTab === tabKey
                  ? 'text-primary-700 -mb-[1px] rounded-t-md border border-primary-300 border-b-secondary-50'
                  : 'text-primary-600 hover:text-primary-800'
              }`}>
              {t(`page.financial.reports.tabs.${tabKey}`)}
            </button>
          ))}
        </div>

        <div className="w-full">
          {activeTab === 'profitLoss' && (
            <FinancialProfitLossPage hideHeader range={range} />
          )}
          {activeTab === 'balance' && (
            <FinancialBalancePage hideHeader bookYearId={range?.mode === 'bookYear' ? range.bookYearId : null} />
          )}
        </div>
      </div>
    </>
  );
}